import { useCallback, useState, useEffect, useRef } from 'react'
import { UseFormSetValue, UseFormWatch } from 'react-hook-form'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
    TourDemoType,
} from '@/utils/tour-styles'
import { CreateTicketFormValues } from './types'

interface TourStep {
    selector: string
    title: string
    description: string
    /** Badge to show condition for conditional fields */
    conditionBadge?: string
    /** Demo animation type */
    demo?: TourDemoType
}

interface UseCreateTicketTourOptions {
    setValue: UseFormSetValue<CreateTicketFormValues>
    watch: UseFormWatch<CreateTicketFormValues>
}

// Demo service ID to trigger conditional fields
const DEMO_SERVICE_ID = 99999

/**
 * Tour hook for Ticket Create form
 * Auto-fills demo values to show ALL fields including conditional ones
 */
export const useCreateTicketTour = (options?: UseCreateTicketTourOptions) => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)
    const originalValuesRef = useRef<Partial<CreateTicketFormValues> | null>(
        null
    )
    // Store options in ref to avoid re-creating callbacks
    const optionsRef = useRef(options)
    optionsRef.current = options

    // All tour steps - will show all fields by forcing demo values
    const tourSteps: TourStep[] = [
        // Section 1: Classification
        {
            selector: '[data-tour="ticket-form"]',
            title: 'Form tạo Ticket',
            description:
                'Form này gồm 5 phần chính:\n• Phân loại: Nhóm và loại ticket\n• Khách hàng & Dịch vụ: Thông tin KH\n• Nội dung: Mô tả vấn đề\n• Độ ưu tiên: SLA và thời gian\n• Phân công: Nhân viên xử lý',
        },
        {
            selector: '[data-tour="ticket-type-group"]',
            title: 'Nhóm Ticket *',
            description:
                'Chọn nhóm ticket phù hợp:\n• Support Requests: Hỗ trợ kỹ thuật\n• Error Service: Sự cố dịch vụ\n• Service Requests: Yêu cầu dịch vụ\n• Change Requests: Yêu cầu thay đổi\n• Internal Problems: Vấn đề nội bộ',
            demo: 'select-dropdown',
        },
        {
            selector: '[data-tour="ticket-type"]',
            title: 'Loại Ticket *',
            description:
                'Chọn loại ticket cụ thể trong nhóm:\n• Danh sách thay đổi theo nhóm đã chọn\n• Ví dụ Error Service: incident, provider, internal...\n• Ảnh hưởng đến các fields hiển thị',
            demo: 'select-dropdown',
        },

        // Section 2: Customer & Service
        {
            selector: '[data-tour="customer-select"]',
            title: 'Khách hàng *',
            description:
                'Tìm và chọn khách hàng:\n• Tìm theo tên công ty\n• Tìm theo email liên hệ\n• Tìm theo số điện thoại\n• Hiển thị avatar và thông tin cơ bản',
            demo: 'customer-select',
        },
        {
            selector: '[data-tour="service-select"]',
            title: 'Dịch vụ *',
            description:
                'Chọn dịch vụ liên quan:\n• Hiển thị danh sách dịch vụ của KH\n• Tự động load thông tin SLA\n• Tính thời gian phản hồi/xử lý\n• Ảnh hưởng TG hoàn thành dự kiến',
            demo: 'service-select',
        },

        // Section 3: Content
        {
            selector: '[data-tour="ticket-content"]',
            title: 'Nội dung Ticket *',
            description:
                'Mô tả chi tiết vấn đề:\n• Hiện tượng xảy ra\n• Thời điểm phát hiện\n• Tác động đến dịch vụ\n• Hỗ trợ đính kèm file/ảnh',
            demo: 'textarea-input',
        },

        // Section 4: Priority & Schedule - Conditional fields
        {
            selector: '[data-tour="ticket-downtime"]',
            title: 'Thời gian Downtime',
            description:
                'Ghi nhận thời gian downtime:\n• Thời điểm bắt đầu/kết thúc\n• Nguyên nhân downtime\n• Dùng tính SLA và báo cáo\n• Ảnh hưởng đánh giá chất lượng',
            conditionBadge: 'Hiện khi Error Service + đã chọn dịch vụ',
            demo: 'datetime-picker',
        },
        {
            selector: '[data-tour="ticket-priority"]',
            title: 'Độ ưu tiên *',
            description:
                'Chọn mức độ ưu tiên:\n• HIGH: Ảnh hưởng nghiêm trọng → Xử lý ngay\n• MEDIUM: Ảnh hưởng một phần → Trong ngày\n• LOW: Không gấp → Có thể lên lịch\n• Ảnh hưởng SLA và escalation',
            demo: 'priority-select',
        },
        {
            selector: '[data-tour="ticket-marjorable"]',
            title: 'Sự cố nghiêm trọng (Major)',
            description:
                'Đánh dấu Major Incident:\n• Sự cố ảnh hưởng nhiều KH\n• Cần escalate lên cấp cao\n• Được theo dõi và báo cáo riêng\n• Quy trình xử lý đặc biệt',
            conditionBadge: 'Hiện khi Error Service Incident + Priority HIGH',
        },
        {
            selector: '[data-tour="ticket-requested-at"]',
            title: 'TG yêu cầu hỗ trợ *',
            description:
                'Thời điểm KH yêu cầu:\n• Mốc bắt đầu tính SLA\n• Dùng để đo response time\n• Mặc định là thời điểm hiện tại\n• Có thể điều chỉnh nếu cần',
            demo: 'datetime-picker',
        },
        {
            selector: '[data-tour="ticket-time-reply"]',
            title: 'TG phản hồi đầu tiên',
            description:
                'Thời điểm phản hồi KH:\n• First response time\n• Tự động điền = TG yêu cầu\n• Dùng đánh giá SLA compliance\n• Quan trọng với priority HIGH',
            demo: 'datetime-picker',
        },
        {
            selector: '[data-tour="ticket-estimated-completion"]',
            title: 'TG hoàn thành dự kiến',
            description:
                'Dự kiến hoàn thành:\n• Tự động tính theo SLA\n• Dựa trên priority + service SLA\n• Có thể điều chỉnh thủ công\n• Deadline cho team xử lý',
            demo: 'datetime-picker',
        },

        // Section 5: Assignees
        {
            selector: '[data-tour="ticket-cs-assignees"]',
            title: 'Account/CS phụ trách',
            description:
                'Nhân viên CS phụ trách:\n• Liên hệ và follow-up với KH\n• Có thể chọn nhiều người\n• Tìm kiếm theo tên\n• Nhận notification về ticket',
            demo: 'assignee-select',
        },
        {
            selector: '[data-tour="ticket-sale-assignees"]',
            title: 'Sales phụ trách',
            description:
                'Nhân viên Sales liên quan:\n• Tự động điền từ thông tin dịch vụ\n• Có thể chỉnh sửa\n• Nhận CC email notification\n• Hỗ trợ escalation nếu cần',
            demo: 'assignee-select',
        },
        {
            selector: '[data-tour="ticket-technical-assignees"]',
            title: 'Technical phụ trách',
            description:
                'Nhân viên kỹ thuật xử lý:\n• Người trực tiếp xử lý ticket\n• Có thể để trống ban đầu\n• Assign sau khi phân tích\n• Nhận notification chính',
            demo: 'assignee-select',
        },

        // Action buttons
        {
            selector: '[data-tour="submit-btn"]',
            title: 'Hoàn thành!',
            description:
                'Tạo ticket mới:\n• Kiểm tra thông tin đã điền\n• Click "Tạo Ticket" để submit\n• Ticket được gửi đến team\n• Nhận mã ticket để theo dõi',
        },
    ]

    const showTourUI = (step: TourStep, stepIndex: number) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found
            if (stepIndex < tourSteps.length - 1) {
                goToStep(stepIndex + 1)
            } else {
                endTour()
            }
            return
        }

        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Build description with condition badge if present
        let fullDescription = step.description
        if (step.conditionBadge) {
            fullDescription = `<span style="display:inline-block;background:#f3e8ff;color:#7c3aed;padding:2px 8px;border-radius:4px;font-size:11px;margin-bottom:8px;">${step.conditionBadge}</span><br/>${step.description}`
        }

        // Wait for scroll then add popover
        setTimeout(() => {
            const popover = createTourPopover({
                title: step.title,
                description: fullDescription,
                currentStep: stepIndex + 1,
                totalSteps: tourSteps.length,
                onPrev:
                    stepIndex > 0 ? () => goToStep(stepIndex - 1) : undefined,
                onNext: () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        goToStep(stepIndex + 1)
                    }
                },
                onClose: endTour,
                isLastStep: stepIndex === tourSteps.length - 1,
                demo: step.demo,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex)
        setCurrentTourStep(stepIndex)
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)

        // Restore original values
        if (originalValuesRef.current && optionsRef.current?.setValue) {
            const { setValue } = optionsRef.current
            const original = originalValuesRef.current

            // Restore all saved fields
            Object.entries(original).forEach(([key, value]) => {
                if (value !== undefined) {
                    setValue(
                        key as keyof CreateTicketFormValues,
                        value as never
                    )
                }
            })

            originalValuesRef.current = null
        }
    }, [])

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)

        // Force demo values to show ALL conditional fields
        if (optionsRef.current?.setValue && optionsRef.current?.watch) {
            const { setValue, watch } = optionsRef.current

            // Store original values for restoration
            originalValuesRef.current = {
                ticket_type_group: watch('ticket_type_group'),
                ticket_type_key: watch('ticket_type_key'),
                ticket_priority: watch('ticket_priority'),
                service_id: watch('service_id'),
            }

            // Set values to trigger ALL conditional fields:
            // - error_service → shows Downtime section
            // - error_service_incident → enables Major checkbox
            // - high priority → shows Major checkbox
            // - service_id > 0 → shows SLA preview & Downtime
            setValue('ticket_type_group', 'error_service')
            setValue('ticket_type_key', 'error_service_incident')
            setValue('ticket_priority', 'high')
            setValue('service_id', DEMO_SERVICE_ID)
        }

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Wait for React to re-render with new values, then start tour
        setTimeout(() => {
            showTourUI(tourSteps[0], 0)
        }, 600)
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive, currentStep: currentTourStep }
}

export default useCreateTicketTour
